import React, { Component } from "react";
import { observer } from "mobx-react";
import qs from "qs";
import urlHelpers from "../../utils/urlHelpers";
import { putRequest } from "../../utils/httpRequest";
import { AxiosError, AxiosResponse } from "axios";

interface PaymentSuccessfulProps {
  match: any;
  handleShowSignInSideBar: (redirectUrl?: string) => void;
  location: any;
}

interface PaymentSuccessfulComponentState extends PaymentSuccessfulState {
  password: string;
  showPassword: boolean;
  isLoading: boolean;
  errors: Array<string>;
  showHelp: boolean;
}

class PaymentSuccessful extends Component<
  PaymentSuccessfulProps,
  PaymentSuccessfulComponentState
> {
  helpWrapRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      userActivationStatus: null,
      redirectUrl: null,
      userEmail: null,
      activationToken: null,
      password: "",
      showPassword: false,
      isLoading: false,
      errors: [],
      showHelp: false,
    };
  }

  componentDidMount() {
    document.body.classList.add("auth-login-page-active");
    document.addEventListener("mousedown", this.handleOutsideHelpClick);

    const queryStringValues = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });
    this.setState({
      userActivationStatus: this.props.match.params.userActivationStatus,
      redirectUrl: queryStringValues.redirectUrl as string,
      userEmail: queryStringValues.userEmail as string,
      activationToken: queryStringValues.activationToken as string,
    });
  }

  componentWillUnmount() {
    document.body.classList.remove("auth-login-page-active");
    document.removeEventListener("mousedown", this.handleOutsideHelpClick);
  }

  renderBlob(className: string) {
    return (
      <div className={`auth-blob ${className}`} aria-hidden="true">
        <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#2E2437"
            d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
          />
          <path
            fill="#2E2437"
            d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
          />
        </svg>
      </div>
    );
  }

  renderCheckIcon() {
    return (
      <div className="result-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12l5 5L20 7" />
        </svg>
      </div>
    );
  }

  renderMailIcon() {
    return (
      <svg
        className="e-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    );
  }

  renderEyeIcon() {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }

  renderEyeOffIcon() {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }

  setPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: event.target.value });
  };

  togglePassword = () => {
    this.setState({ showPassword: !this.state.showPassword });
  };

  toggleHelp = () => {
    this.setState({ showHelp: !this.state.showHelp });
  };

  handleOutsideHelpClick = (event: MouseEvent) => {
    const helpWrap = this.helpWrapRef.current;

    if (!this.state.showHelp || !helpWrap) {
      return;
    }

    if (!helpWrap.contains(event.target as Node)) {
      this.setState({ showHelp: false });
    }
  };

  attemptActivation = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.state.password.length < 8) {
      this.setState({ errors: ["Password needs at least 8 characters."] });
      return;
    }

    const url = urlHelpers.api.userActivations.updateUserActivationsPath(
      this.state.activationToken,
    );

    this.setState({
      errors: [],
      isLoading: true,
    });

    const requestData = {
      user: {
        password: this.state.password,
        password_confirmation: this.state.password,
        email: this.state.userEmail,
      },
    };

    putRequest(url, requestData)
      .then((response: AxiosResponse) => {
        if (response.data.success) {
          window.location.href = this.state.redirectUrl;
        }
      })
      .catch((error: AxiosError<{ errors: string[] }>) => {
        this.setState({
          isLoading: false,
          errors: error.response?.data?.errors || [
            "Something went wrong. Please try again.",
          ],
        });
      });
  };

  renderErrors() {
    if (!this.state.errors || this.state.errors.length === 0) {
      return null;
    }

    return (
      <div className="field-error" id="payment-password-error" role="alert">
        {this.state.errors.map((msg, index) => {
          if (msg.toLocaleLowerCase() === "your activation token has expired") {
            return (
              <div key={index}>
                {msg}. Please contact{" "}
                <a href="mailto:groundcontrol@42courses.com?subject=Can't activate my account, help!">
                  groundcontrol@42courses.com
                </a>{" "}
                for help.
              </div>
            );
          }

          return <div key={index}>{msg}</div>;
        })}
      </div>
    );
  }

  renderForDeactiveStatus() {
    if (this.state.activationToken) {
      return (
        <>
          <div className="pay-head">
            {this.renderCheckIcon()}
            <h1 className="pay-title pay-hero" id="pay-title">
              Payment confirmed.
            </h1>
            <p className="pay-welcome">
              Welcome to 42courses. You are going to enjoy this.
            </p>
            <p className="pay-sub">
              Pick a password and your account is ready.
            </p>
          </div>

          <form
            className="pay-form"
            noValidate={true}
            onSubmit={this.attemptActivation}
          >
            <div className="email-confirmed">
              {this.renderMailIcon()}
              <span className="e-text">
                <span className="e-lbl">Account email</span>
                <span className="e-addr">{this.state.userEmail}</span>
              </span>
            </div>

            <div className="field password-field">
              <label htmlFor="pay-pwd" className="visually-hidden">
                Choose a password
              </label>
              <input
                type={this.state.showPassword ? "text" : "password"}
                id="pay-pwd"
                name="password"
                placeholder="Choose a password (8+ characters)"
                autoComplete="new-password"
                minLength={8}
                required
                value={this.state.password}
                onChange={this.setPassword}
                aria-invalid={this.state.errors.length > 0}
                aria-describedby={
                  this.state.errors.length > 0
                    ? "payment-password-error"
                    : undefined
                }
              />
              <button
                type="button"
                className={`pwd-toggle ${this.state.showPassword ? "is-shown" : ""}`}
                aria-label={
                  this.state.showPassword ? "Hide password" : "Show password"
                }
                onClick={this.togglePassword}
              >
                <span className="icon-show">{this.renderEyeIcon()}</span>
                <span className="icon-hide">{this.renderEyeOffIcon()}</span>
              </button>
            </div>

            {this.renderErrors()}

            <button
              type="submit"
              className={`btn btn-blue ${this.state.isLoading ? "loading" : ""}`}
              disabled={this.state.isLoading}
            >
              {this.state.isLoading
                ? "Setting up your account..."
                : "Create my account"}
            </button>
          </form>

          <p className="email-help">
            Wrong email?{" "}
            <a href="mailto:groundcontrol@42courses.com">Get in touch.</a>
          </p>
        </>
      );
    } else {
      return (
        <>
          <div className="pay-head">
            {this.renderCheckIcon()}
            <h1 className="pay-title pay-hero" id="pay-title">
              Payment confirmed.
            </h1>
            <p className="pay-welcome">
              Check your email to finish setting up your account.
            </p>
          </div>
          <p className="pay-sub">
            We sent an activation link to{" "}
            <strong>{this.state.userEmail}</strong>.
          </p>
          <p className="email-help">
            Need help?{" "}
            <a
              href={`mailto:groundcontrol@42courses.com?subject=Can't activate my account for ${this.state.userEmail}, help!`}
            >
              Contact groundcontrol@42courses.com
            </a>
            .
          </p>
        </>
      );
    }
  }

  renderForActivationStatus() {
    switch (this.state.userActivationStatus) {
      case "activated":
        return (
          <>
            <div className="pay-head">
              {this.renderCheckIcon()}
              <h1 className="pay-title pay-hero" id="pay-title">
                Payment confirmed.
              </h1>
              <p className="pay-welcome">Welcome back to 42courses.</p>
            </div>
            <p className="pay-sub">
              Looks like you already have an account with us under{" "}
              <strong>{this.state.userEmail}</strong>
            </p>
            <button
              type="button"
              className="btn btn-blue"
              onClick={() =>
                this.props.handleShowSignInSideBar(this.state.redirectUrl)
              }
            >
              Log in to continue
            </button>
          </>
        );
      case "deactivated":
        return this.renderForDeactiveStatus();
      default:
        return null;
    }
  }

  render() {
    return (
      <div className="ui pages" id="payment_success">
        <a href="#pay-card" className="skip-link">
          Skip to the form
        </a>
        <div className="auth-page">
          {this.renderBlob("one")}
          {this.renderBlob("two")}

          <div className="help-wrap" ref={this.helpWrapRef}>
            <button
              type="button"
              className="help-link"
              aria-expanded={this.state.showHelp}
              aria-controls="payment-help-pop"
              onClick={this.toggleHelp}
            >
              Need a hand?
            </button>
            <div
              className="help-pop"
              id="payment-help-pop"
              role="dialog"
              aria-label="Need a hand?"
              hidden={!this.state.showHelp}
            >
              <p>
                Money taken but no email arrived? Reach us at{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>{" "}
                and we will sort it.
              </p>
            </div>
          </div>

          <main className="auth-main">
            <section
              className="pay-card"
              id="pay-card"
              aria-labelledby="pay-title"
            >
              {this.renderForActivationStatus()}
            </section>
          </main>

          <footer className="auth-footer">
            Made with care in London. &nbsp;&middot;&nbsp;{" "}
            <a href="/">Back to 42courses</a>
          </footer>
        </div>
      </div>
    );
  }
}

export default observer(PaymentSuccessful);
